import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Search,
  CheckCircle2,
  XCircle,
  Users,
  MessageSquare,
  Send,
  X,
  AlertCircle,
  Loader,
  CalendarOff,
  LayoutTemplate,
} from "lucide-react";
import { attendanceApi, studentApi, calendarApi } from "../../api/services";
import { useData } from "../../contexts/DataContext";
import SettingsHeader from "../../ui/SettingsHeader";
import ConfirmationModal from "../../ui/ConfirmationModal";
import LoadingOverlay from "../../ui/LoadingOverlay";
import RecipientSelector from "../../components/Common/RecipientSelector";

const AttendanceTaking = ({ selectedClass, selectedSection, selectedDate, onBack }) => {
  const { currentAcademicYear, whatsappConfigured } = useData();
  const [attendance, setAttendance] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [attendanceTaken, setAttendanceTaken] = useState(false);
  const [isHoliday, setIsHoliday] = useState(false);
  const [holidayEvent, setHolidayEvent] = useState(null);

  // WhatsApp States
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showHolidayConfirmModal, setShowHolidayConfirmModal] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [selectedForWhatsApp, setSelectedForWhatsApp] = useState(new Set());
  const [targetAudience, setTargetAudience] = useState(() => {
    const orgType = localStorage.getItem('organizationType')?.toUpperCase();
    return orgType === 'COLLEGE' ? 'both' : 'parent';
  });
  const [initialLoading, setInitialLoading] = useState(true);

  const changesHistory = useRef([]);

  const [stats, setStats] = useState({ present: 0, absent: 0, total: 0 });

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  const checkHolidayFromCalendar = async (date) => {
    try {
      const dateStr = new Date(date).toISOString().split('T')[0];
      const response = await calendarApi.getEventsByRange(dateStr, dateStr);
      
      if (response?.success && response.data) {
        const events = Array.isArray(response.data) ? response.data : [];
        const holidayEvent = events.find(event => 
          event.eventType === 'holiday' || 
          event.type === 'holiday' ||
          event.isHoliday === true
        );
        
        if (holidayEvent) {
          setHolidayEvent(holidayEvent);
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!selectedClass || !selectedDate) return;
      setInitialLoading(true);
      try {
        const isCalendarHoliday = await checkHolidayFromCalendar(selectedDate);
        const sectionId = selectedSection ? selectedSection._id : null;

        const [studentsRes, attendanceRes] = await Promise.all([
          studentApi.getStudentsByUnit(selectedClass._id, currentAcademicYear, sectionId),
          attendanceApi.getDayAttendance(selectedDate, selectedClass._id, currentAcademicYear, sectionId),
        ]);

        let existingRecords = [];
        let isDayHoliday = isCalendarHoliday;
        let isNotifSent = false;
        let isTaken = false;

        if (attendanceRes?.success && Array.isArray(attendanceRes.data) && attendanceRes.data.length > 0) {
          existingRecords = attendanceRes.data;
          isTaken = true;
          isDayHoliday = existingRecords[0].isHoliday || isDayHoliday;
          isNotifSent = existingRecords.some((r) => r.notificationSent);
        }

        let studentList = [];
        if (studentsRes?.success) {
          studentList = studentsRes.data.map((s) => {
            const record = existingRecords.find(
              (r) => (s.currentEnrollment?._id && r.studentEnrollmentId === s.currentEnrollment._id) || r.studentId === s._id
            );
            const status = record ? (record.status || "present").toLowerCase() : "present";
            return {
              studentId: s._id,
              studentRegistrationId: s.studentRegistrationId,
              studentEnrollmentId: s.currentEnrollment?._id,
              studentName: `${s.firstName} ${s.lastName}`.trim(),
              phone: s.primaryContactNumber || s.phone || s.parentPhone,
              status,
              hasRecord: !!record,
            };
          });
        }
  
        setAttendanceTaken(isTaken);
        setWhatsappSent(isNotifSent);
        setIsHoliday(isDayHoliday);
        setAttendance(studentList);
        changesHistory.current = [];
        computeStats(studentList);
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [selectedClass, selectedDate, selectedSection, currentAcademicYear]);

  const computeStats = (list) => {
    const s = { present: 0, absent: 0, total: list.length };
    list.forEach((item) => {
      if (item.status === "present") s.present++;
      if (item.status === "absent") s.absent++;
    });
    setStats(s);
  };

  const toggleStatus = (studentId) => {
    if (isHoliday) return;
    const student = attendance.find((s) => s.studentId === studentId);
    const newStatus = student.status === "present" ? "absent" : "present";
    const updated = attendance.map((s) =>
      s.studentId === studentId ? { ...s, status: newStatus } : s
    );
    setAttendance(updated);
    computeStats(updated);
  };

  const filtered = attendance.filter((s) => {
    const q = searchTerm.toLowerCase();
    return (
      s.studentName.toLowerCase().includes(q) ||
      s.studentRegistrationId.toLowerCase().includes(q)
    );
  });

  const toggleStudentSelection = (studentId) => {
    const newSelected = new Set(selectedForWhatsApp);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedForWhatsApp(newSelected);
  };

  const handleSendWhatsApp = async () => {
    if (selectedForWhatsApp.size === 0) return;
    setSendingWhatsApp(true);
    try {
      const studentsToSend = attendance.filter((s) =>
        selectedForWhatsApp.has(s.studentId)
      );
      
      const payload = {
        date: new Date(selectedDate).toISOString(),
        students: studentsToSend.map(s => ({
          studentId: s.studentId,
          studentName: s.studentName,
          phone: s.phone,
          status: s.status
        })),
        unitId: selectedClass._id,
        targetAudience: targetAudience
      };

      const result = await attendanceApi.sendNotifications(payload);
      if (result?.success) {
        toast.success(`Sent ${result.data.sent} of ${result.data.total} WhatsApp notifications`);
        setWhatsappSent(true);
        setShowWhatsAppModal(false);
        setSelectedForWhatsApp(new Set());
      }
    } catch (error) {
      toast.error("Failed to send notifications");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleMarkHoliday = async () => {
    setShowHolidayConfirmModal(true);
  };

  const confirmMarkHoliday = async () => {
    setLoading(true);
    try {
      const result = await attendanceApi.markHoliday({
        date: new Date(selectedDate).toISOString(),
        isHoliday: !isHoliday,
        unitId: selectedClass._id
      });
      if (result?.success) {
        setIsHoliday(!isHoliday);
        toast.success(isHoliday ? "Holiday removed" : "Holiday marked");
      }
    } catch (error) {
      toast.error("Failed to update holiday status");
    } finally {
      setLoading(false);
      setShowHolidayConfirmModal(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        date: new Date(selectedDate).toISOString(),
        isHoliday,
        unitId: selectedClass._id, 
        sectionId: selectedSection ? selectedSection._id : undefined,
        targetAudience: targetAudience,
        records: attendance.map((s) => ({
          studentId: s.studentId,
          studentEnrollmentId: s.studentEnrollmentId,
          studentRegistrationId: s.studentRegistrationId,
          studentName: s.studentName,
          phone: s.phone,
          status: isHoliday ? "absent" : s.status,
        })),
      };
      const result = await attendanceApi.bulkMarkAttendance(payload);
      if (result?.data?.count) {
        toast.success(`Attendance saved! ${result.data.count} WhatsApp alerts sent.`);
      } else {
        toast.success("Attendance saved successfully");
      }
      setAttendanceTaken(true);
      setWhatsappSent(true);
    } catch (error) {
      toast.error(error.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="relative w-full h-[calc(100vh-7rem)] flex flex-col bg-gray-50/30 animate-in fade-in duration-500 overflow-hidden rounded-2xl border border-gray-200/50 shadow-sm">
      {(loading || sendingWhatsApp) && (
        <LoadingOverlay 
          message={sendingWhatsApp ? "Broadcasting Alerts" : "Updating Attendance"} 
          subtext={sendingWhatsApp ? "Sending WhatsApp messages to parents..." : "Securing records in academic database..."}
        />
      )}

      {/* Fixed Header Section */}
      <div className="flex-none p-4 pb-0 lg:p-6 lg:pb-0 z-10 bg-white/50 backdrop-blur-sm">
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-8 space-y-4">
          <SettingsHeader
            title="Attendance Tracking"
            description={`${(selectedClass?.unitName || selectedClass?.name || '').toUpperCase()} • ${(selectedSection?.name || selectedSection?.sectionName || (typeof selectedSection === 'string' ? selectedSection : '')) || 'General'} • ${formatDate(selectedDate)}`}
            showBack={true}
            onBack={onBack}
            icon={Users}
            buttons={[
              {
                label: isHoliday ? "Holiday Marked" : "Mark Holiday",
                icon: CalendarOff,
                onClick: handleMarkHoliday,
                variant: isHoliday ? "secondary" : "outline",
                className: isHoliday ? "!bg-amber-50 !text-amber-700 !border-amber-100 !rounded-lg" : "!rounded-lg shadow-sm !bg-white !text-gray-700 hover:!bg-gray-50 !py-2 !px-4 !text-xs font-semibold"
              },
              ...(!isHoliday && whatsappConfigured ? [{
                label: "Broadcast Alerts",
                icon: MessageSquare,
                onClick: () => setShowWhatsAppModal(true),
                variant: "outline",
                className: "!rounded-lg !bg-white !text-gray-700 !border-gray-200 hover:!bg-gray-50 shadow-sm !py-2 !px-4 !text-xs font-semibold"
              }] : []),
              {
                label: attendanceTaken ? "Update Records" : "Save Attendance",
                icon: attendanceTaken ? CheckCircle2 : Send,
                onClick: handleSave,
                variant: "default",
                className: "!rounded-lg shadow-md hover:shadow-lg !py-2 !px-6 !bg-gray-900 !text-white !text-xs font-bold uppercase tracking-wider transition-all transform hover:-translate-y-0.5"
              },
            ]}
          />

          {/* Holiday Event Banner - Fixed if present */}
          {holidayEvent && isHoliday && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-3 sm:p-4 shadow-sm animate-in fade-in slide-in-from-top-2">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="p-2.5 bg-amber-100 rounded-full shadow-sm">
                  <CalendarOff className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-amber-900 text-sm flex items-center gap-2 flex-wrap">
                    🎉 {holidayEvent.title || holidayEvent.eventName || 'Holiday'}
                    <span className="px-2.5 py-0.5 bg-amber-200 text-amber-800 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      Calendar Event
                    </span>
                  </h3>
                  {holidayEvent.description && (
                    <p className="text-xs text-amber-700 mt-1">{holidayEvent.description}</p>
                  )}
                  <p className="text-xs text-amber-600/80 mt-2 font-medium">
                    • All students are automatically marked as absent for this holiday.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area - Scrollable on mobile, Fixed grid on desktop */}
      <div className="flex-1 min-h-0 p-4 lg:p-6 lg:pt-4 overflow-y-auto lg:overflow-hidden">
        <div className="h-full max-w-full mx-auto px-2 sm:px-4 lg:px-8">
          <div className="h-full grid grid-cols-1 lg:grid-cols-4 gap-6">
            
            {/* Left Column: Stats & Search - Fixed & Scrollable on Desktop */}
            <div className="lg:col-span-1 lg:h-full lg:overflow-y-auto custom-scrollbar lg:pr-2">
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 space-y-4 sm:space-y-5">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Overview</h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between p-3 bg-emerald-50/50 rounded-xl border border-emerald-100/50">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                          <CheckCircle2 size={16} />
                        </div>
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Present</span>
                      </div>
                      <span className="text-xl font-black text-emerald-600">{stats.present}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-rose-50/50 rounded-xl border border-rose-100/50">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-rose-100 text-rose-600 rounded-lg">
                          <XCircle size={16} />
                        </div>
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Absent</span>
                      </div>
                      <span className="text-xl font-black text-rose-600">{stats.absent}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white border border-gray-200 text-gray-400 rounded-lg shadow-sm">
                          <Users size={16} />
                        </div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Total</span>
                      </div>
                      <span className="text-xl font-black text-gray-700">{stats.total}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: List - Fixed Container & Internal Scroll on Desktop */}
            <div className="lg:col-span-3 lg:h-full lg:flex lg:flex-col lg:min-h-0">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col h-[500px] lg:h-full">
                {/* Search Bar Header */}
                <div className="flex-none p-4 border-b border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50/30 rounded-t-2xl">
                  <div className="relative flex-1 w-full sm:max-w-md">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search student..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all shadow-sm placeholder:text-gray-400"
                    />
                  </div>
                  <div className="text-xs font-medium text-gray-400">
                    Showing {filtered.length} students
                  </div>
                </div>

                <div className="flex-1 overflow-auto custom-scrollbar relative">
                  <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 bg-white shadow-sm z-10">
                      <tr className="bg-gray-50/90 border-b border-gray-100 backdrop-blur-sm">
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest w-12 sm:w-16">#</th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Student Information</th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filtered.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="py-20 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-50 mb-4">
                              <Search className="w-6 h-6 text-gray-300" />
                            </div>
                            <p className="text-sm font-medium text-gray-500">No students found matching your search.</p>
                          </td>
                        </tr>
                      ) : filtered.map((student, idx) => (
                        <tr key={student.studentId} className="group hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs font-bold text-gray-400">
                            {String(idx + 1).padStart(2, '0')}
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="flex items-center gap-3 sm:gap-4">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 border border-white shadow-sm flex items-center justify-center font-bold text-xs sm:text-sm text-gray-500 group-hover:scale-105 transition-transform flex-shrink-0">
                                {student.studentName.charAt(0)}
                              </div>
                              <div className="min-w-0">
                                <h4 className="text-xs sm:text-sm font-bold text-gray-900 group-hover:text-indigo-600 transition-colors truncate">{student.studentName}</h4>
                                <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-0.5">
                                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded truncate">{student.studentRegistrationId}</span>
                                  {student.phone && <span className="text-[9px] sm:text-[10px] font-medium text-gray-400 px-1.5 py-0.5 hidden sm:inline-block">📞 {student.phone}</span>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4 text-right whitespace-nowrap">
                            {isHoliday ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wider border border-amber-100">
                                <CalendarOff size={10} className="sm:w-3 sm:h-3" /> <span className="hidden sm:inline">Holiday</span> <span className="sm:hidden">OFF</span>
                              </span>
                            ) : (
                              <div className="inline-flex bg-gray-100 p-1 rounded-lg border border-gray-200 shadow-inner">
                                <button
                                  onClick={() => student.status !== "present" && toggleStatus(student.studentId)}
                                  className={`px-2 sm:px-4 py-1.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-all shadow-sm ${
                                    student.status === "present"
                                      ? "bg-white text-emerald-600 shadow-sm ring-1 ring-black/5"
                                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50 shadow-none"
                                  }`}
                                >
                                  P
                                  <span className="hidden sm:inline">resent</span>
                                </button>
                                <button
                                  onClick={() => student.status !== "absent" && toggleStatus(student.studentId)}
                                  className={`px-2 sm:px-4 py-1.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-all shadow-sm ${
                                    student.status === "absent"
                                      ? "bg-white text-rose-600 shadow-sm ring-1 ring-black/5"
                                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50 shadow-none"
                                  }`}
                                >
                                  A
                                  <span className="hidden sm:inline">bsent</span>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWhatsAppModal && !isHoliday && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-md transition-all duration-300">
          <div className="w-full max-w-2xl bg-white shadow-[0_20px_50px_rgba(0,0,0,0.2)] rounded-3xl flex flex-col max-h-[92vh] overflow-hidden border border-gray-100 animate-in zoom-in-95 duration-300">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-white sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 leading-tight font-display">
                    Broadcast Alerts
                  </h3>
                  <p className="text-xs text-gray-500 font-medium tracking-tight">Send official attendance updates via WhatsApp</p>
                </div>
              </div>
              <button 
                onClick={() => setShowWhatsAppModal(false)} 
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 custom-scrollbar bg-gray-50/20">
              {whatsappSent && (
                <div className="mb-6 px-4 py-3 bg-amber-50/70 border border-amber-100 rounded-2xl flex items-start gap-4">
                  <div className="p-1.5 bg-white rounded-lg shadow-sm">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xs text-amber-900">Notifications Already Dispatched</h4>
                    <p className="text-[11px] text-amber-700/80 mt-0.5 font-medium leading-relaxed">
                      Alerts for today's session have already been processed. Sending again will result in duplicate messages.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="mb-8">
                <RecipientSelector
                  value={targetAudience}
                  onChange={setTargetAudience}
                  label="Recipient Audience"
                  stepNumber={1}
                  helperText="Select which group should receive the attendance alerts"
                />
              </div>
              
              <div className="flex items-center gap-4 mb-4 mt-8 px-1">
                <div className="flex items-center gap-2.5">
                  <div className="w-1.5 h-6 bg-gray-900 rounded-full" />
                  <h3 className="text-[12px] font-bold text-gray-900 uppercase tracking-widest">
                    2. Select Students (Absentees Only)
                  </h3>
                </div>
                <div className="flex-1 h-px bg-gray-100" />
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setSelectedForWhatsApp(new Set(attendance.filter(s => s.status === 'absent').map(s => s.studentId)))}
                    className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    Select All
                  </button>

                  <button
                    onClick={() => setSelectedForWhatsApp(new Set())}
                    className="text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm bg-white">
                <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr className="border-b border-gray-100">
                        <th className="px-5 py-4 text-left w-12">
                          <div className="flex items-center justify-center">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select</span>
                          </div>
                        </th>
                        <th className="px-2 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Student Information</th>
                        <th className="px-2 py-4 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Attendance</th>
                        <th className="px-5 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Contact</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {attendance.filter(s => s.status === 'absent').length === 0 ? (
                        <tr>
                          <td colSpan="4" className="py-8 text-center text-gray-400 font-medium">
                            No absent students found for this date.
                          </td>
                        </tr>
                      ) : (
                        attendance.filter(s => s.status === 'absent').map((student) => {
                          const isSelected = selectedForWhatsApp.has(student.studentId);
                          return (
                            <tr 
                              key={student.studentId} 
                              className={`transition-all duration-200 ${isSelected ? 'bg-indigo-50/30' : 'bg-transparent hover:bg-white'}`}
                              onClick={() => toggleStudentSelection(student.studentId)}
                            >
                              <td className="px-5 py-3 ml-2">
                                <div className="flex items-center justify-center">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}} 
                                    className="w-4 h-4 rounded border-gray-300 transition-all cursor-pointer accent-indigo-600"
                                  />
                                </div>
                              </td>
                              <td className="px-2 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center font-bold text-[10px] text-gray-500 shadow-sm">
                                    {student.studentName.charAt(0)}
                                  </div>
                                  <div>
                                    <p className="font-bold text-gray-800 text-xs">{student.studentName}</p>
                                    <p className="text-[9px] text-gray-400 font-medium">{student.studentRegistrationId}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 py-3 text-center">
                                <span className="px-2 py-1 text-[9px] font-black uppercase tracking-widest rounded-lg bg-rose-50 text-rose-600 border border-rose-100">
                                  {student.status}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-right">
                                <span className="text-[10px] text-gray-500 font-mono font-bold bg-white px-2 py-1 rounded border border-gray-100">
                                  {student.phone || "---"}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 bg-white sticky bottom-0 flex justify-between items-center gap-4">
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="px-6 py-3 font-bold text-gray-500 hover:text-gray-900 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all text-xs uppercase tracking-widest"
              >
                Cancel
              </button>
              <button
                onClick={handleSendWhatsApp}
                disabled={sendingWhatsApp || selectedForWhatsApp.size === 0}
                className="flex-1 max-w-xs flex items-center justify-center gap-3 px-8 py-3.5 bg-[var(--primary-color)] text-white rounded-xl hover:opacity-90 transition-all text-xs font-bold uppercase tracking-widest disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed shadow-md"
                style={{ backgroundColor: 'var(--primary-color)' }}
              >
                <Send className={`w-4 h-4 ${sendingWhatsApp ? 'animate-pulse' : ''}`} /> 
                {sendingWhatsApp ? 'Sending...' : `Send to ${selectedForWhatsApp.size} ${selectedForWhatsApp.size === 1 ? 'Recipient' : 'Recipients'}`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmationModal
        isOpen={showHolidayConfirmModal}
        onClose={() => setShowHolidayConfirmModal(false)}
        onConfirm={confirmMarkHoliday}
        title={isHoliday ? "Remove Holiday?" : "Mark as Holiday?"}
        message={isHoliday
          ? "This will remove the holiday status. Attendance records will be preserved."
          : "This will mark all students as 'Absent' for this day. Existing attendance records will be overwritten."}
        confirmText="Confirm"
        variant={isHoliday ? "success" : "warning"}
      />
      </div>
  );
};

export default AttendanceTaking;
